import { applyLinkSafetyToDom } from '@/lib/email/linkSafety';

/**
 * Format date to relative time or short date.
 *
 * `timeZone` is the display timezone (from `useDisplayTimezone()`) used when
 * the date is old enough to render as an absolute date rather than a
 * relative offset.
 */
export function formatDate(dateStr: string, timeZone: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    }
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    if (diffDays < 7) {
      return `${diffDays}d ago`;
    }

    return date.toLocaleDateString('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return dateStr;
  }
}

/**
 * Decode HTML entities in text.
 */
export function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Normalize a raw email body to HTML and detect quoted content, without
 * hiding it. Pure — independent of whether quoted content is shown.
 */
export function parseEmailBody(bodyContent: string): {
  processedBody: string;
  hasQuoted: boolean;
} {
  const isPlainText = !/<[^>]+>/.test(bodyContent);

  let processedBody = bodyContent;
  if (isPlainText) {
    processedBody = bodyContent
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('<br>');
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedBody;
  const hasQuoted =
    tempDiv.querySelectorAll('blockquote, .gmail_quote, .quoted-text').length >
    0;

  return { processedBody, hasQuoted };
}

/**
 * Build the display HTML for the iframe, hiding quoted content unless
 * showQuoted is true, and applying link safety.
 */
export function buildDisplayHtml(
  processedBody: string,
  showQuoted: boolean
): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedBody;

  if (!showQuoted) {
    tempDiv
      .querySelectorAll('blockquote, .gmail_quote, .quoted-text')
      .forEach(quote => {
        (quote as HTMLElement).style.display = 'none';
      });
  }

  applyLinkSafetyToDom(tempDiv);

  return tempDiv.innerHTML;
}

/**
 * Extract name from email "Name <email@example.com>" format.
 */
export function extractName(from: string): string {
  const match = from.match(/^([^<]+)\s*</);
  if (match) {
    return match[1].trim();
  }
  const emailMatch = from.match(/^([^@]+)@/);
  if (emailMatch) {
    return emailMatch[1].trim();
  }
  return from;
}
